/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import AudioPlayer from "../Services/AudioPlayer";
import AudiosService from "../Services/Audios";
import VideoService from "../Services/VideoPlayer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as CryptoJS from "crypto-js";
import { clearAudioInterrupt } from "../utils/mediaMutex";

export interface IAudioItem {
  ctime: number;
  id: string;
  name: string;
  author: string;
  playing: boolean;
  audioPath: string;
  audioUrl: string;
  pravachanPath: string;
  pravachanUrl: string;
  lyricsPath: string;
  lyricsHash: string;
  index: number;
  slug: string;
  audioLength: number;
  description: string;
  imagePath: string;
}
interface IAudiosContextProps {
  isMin: boolean,
  setMin: React.Dispatch<React.SetStateAction<boolean>>;
  isMinimise: boolean;
  setIsMinismise: React.Dispatch<React.SetStateAction<boolean>>;
  audiosList: IAudioItem[];
  currentAudio: IAudioItem | null;
  setCurrentAudio: any;
  setAudiosList: React.Dispatch<React.SetStateAction<IAudioItem[]>>;
  playAudio: (id: string, index: number, audioSlug: string) => void;
  close: () => void;
  next: () => void;
  prev: () => void;
  favaddremove: () => void;
  isLiked: boolean,
  message: string,
  lyrt: () => string;
  playbackspeed: (e: any) => void;
  showList: boolean;
  setShowList: React.Dispatch<React.SetStateAction<boolean>>;
  playingAudio: boolean;
  setPlayingAudio: any;
  playVideo: boolean;
  setPlayVideo: any;
  refAudio: any;
  state: any
  ,
  lyricsText: string;
  reloadLyrics: () => void;
  playPravachan: any;
  setpravachanId: React.Dispatch<React.SetStateAction<string>>;
  setMbAudioId: React.Dispatch<React.SetStateAction<string>>;
}

const AudioContext = React.createContext<IAudiosContextProps>({} as any);
export function AudioProvider(props: { children: any }) {
  const [currentAudio, setCurrentAudio] = useState<IAudioItem | null>(null);
  const [audiosList, setAudiosList] = useState<IAudioItem[]>([]);
  // removed unused isLoaded state
  const [audioIndex, setAudioIndex] = useState<any>(0);
  const [lyrtxt, setlyrtxt] = useState("");
  const [audioId, setaudioId] = useState("");
  const [Type, setType] = useState<any | undefined>(undefined);
  const [isMinimise, setIsMinismise] = useState<boolean>(false);
  const [isMin, setMin] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(true);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [message, setMessage] = useState("");
  const [playingAudio, setPlayingAudio] = useState<boolean>(false);
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const [isLoading, setLoading] = useState(false);
  const [pravachanId, setpravachanId] = useState<string>("");
  const [mbAudioId, setMbAudioId] = useState<string>("");
  const navigate = useNavigate();
  const refAudio = useRef(null);
  // Tracks last successfully loaded audio id — avoids re-fetch/restart on route revisit
  const lastFetchedAudioIdRef = useRef<string>("");
  const location = useLocation();
  const state = location.state as {
    audioId: string,
    audioslug: string,
    sorting: number,
    index: number,
    audiocat: string,
    audioAuthor: string,
    /** Kept when opened from SearchDataPage — must survive playAudio re-navigate */
    searched?: string,
    searchOrigin?: { product: string; searchtext: string; author: string },
  }

  /**
   * Route sync inside full player must not drop search-return payload.
   * Without this, Back/Close fall back to /audios|/pravachans instead of search.
   */
  const buildPlayNavigateState = (id: string, index: number, slug: string) => {
    const prev = (location.state as Record<string, unknown> | null) || {};
    return {
      ...prev,
      audioId: id,
      audioslug: slug,
      index,
    };
  };

  const playAudio = (id: string, index: number, audioSlug: string) => {
    const logdetails: string[] = [];
    logdetails.push(`id=${id}`);
    logdetails.push(`index=${index}`);
    logdetails.push(`audioSlug=${audioSlug || "none"}`);
    logdetails.push(`stateAudioId=${state?.audioId || "none"}`);
    logdetails.push(`currentId=${currentAudio?.id || "none"}`);
    logdetails.push(`audioIdState=${audioId || "none"}`);
    logdetails.push(`has_search_origin=${Boolean(state?.searched)}`);

    const currentTime = new Date().toISOString();
    if (typeof index != "number") {
      index = 0;
    }

    // Same track already active — keep playback position; only sync index + route
    const isSameTrack = !!(id && (currentAudio?.id === id || audioId === id));
    logdetails.push(`isSameTrack=${isSameTrack}`);

    if (isSameTrack) {
      setAudioIndex(index);
      logdetails.push("action=skip_reload_keep_playback");
      console.log("[AudioContext] playAudio", logdetails.join(" | "));
      if (isMinimise === false) {
        const slugToUse = audioSlug || currentAudio?.slug || "";
        if (slugToUse) {
          navigate(
            `/${localStorage.getItem("type")}/${encodeURIComponent(slugToUse)}`,
            { state: buildPlayNavigateState(id, index, slugToUse) }
          );
        }
      }
      return;
    }

    setAudioIndex(index);
    // Immediately set currentAudio from the loaded list when possible so that
    // UI updates (including RabbitLyrics initialization) can happen prior to
    // the API response that fetches the full audio object by id. This mirrors
    // the behavior performed by next/prev which already call setCurrentAudio.
    try {
      // Prefer index (if number), otherwise try to find by id in the list
      let newAudio;
      if (typeof index === "number" && audiosList && audiosList[index]) {
        newAudio = audiosList[index];
      } else if (id && audiosList && audiosList.length > 0) {
        newAudio = audiosList.find((a) => a.id === id);
      }
      if (newAudio) {
        // Clear any previous lyrics immediately so the UI doesn't flash
        // lyrics belonging to the previously playing audio while the
        // new audio's lyrics are being loaded and RabbitLyrics initializes.
        try {
          setlyrtxt("");
        } catch (err) {
          console.warn("[AudioContext] setlyrtxt not available to clear lyrics", err);
        }
        setCurrentAudio(newAudio);
      }
    } catch (err) {
      console.warn("[AudioContext] playAudio: error picking audio from audiosList", err);
    }
    // Force loader to treat this as a new fetch target
    lastFetchedAudioIdRef.current = "";
    setaudioId(id);
    logdetails.push("action=load_new_track");
    console.log("[AudioContext] playAudio", logdetails.join(" | "));
    const slugForNav = audioSlug || currentAudio?.slug || "";
    if (isMinimise === false && slugForNav) {
      navigate(
        `/${localStorage.getItem("type")}/${encodeURIComponent(slugForNav)}`,
        { state: buildPlayNavigateState(id, index, slugForNav) }
      );
    }
    // useractivity requires auth; 401 would reload the page via ApiUtility — skip for guests
    const token = localStorage.getItem("Token");
    const userId = localStorage.getItem("UserId");
    if (token && userId) {
      let url: any = localStorage.getItem("type")
      VideoService.updateUserActivity(url, id, currentTime).then((response) => {
        console.log("[AudioContext] updateUserActivity response", { url, id, response });
      }).catch((err) => {
        console.error("[AudioContext] updateUserActivity error", err);
      })
    } else {
      console.log("[AudioContext] updateUserActivity skipped — user not logged in");
    }
  };

  const close = () => {
    console.log("[AudioContext] close called -> clearing audio state");
    clearAudioInterrupt();
    lastFetchedAudioIdRef.current = "";
    setAudiosList([]);
    setaudioId("");
    setCurrentAudio(null);
  };

  // non-wrapping next (your style)
const next = () => {
  if (!audiosList || audiosList.length === 0) return;

  setAudioIndex((prevIndex: number) => {
    if (prevIndex >= audiosList.length - 1) return prevIndex;
    const nextIndex = prevIndex + 1;
    const nextAudio = audiosList[nextIndex];
    setCurrentAudio(nextAudio);
    playAudio(nextAudio.id, nextIndex, nextAudio.slug);
    return nextIndex;
  });
};

// prev implemented in the same style (non-wrapping)
const prev = () => {
  if (!audiosList || audiosList.length === 0) return;

  setAudioIndex((prevIndex: number) => {
    if (prevIndex <= 0) return prevIndex;
    const newIndex = prevIndex - 1;
    const newAudio = audiosList[newIndex];
    setCurrentAudio(newAudio);
    playAudio(newAudio.id, newIndex, newAudio.slug);
    return newIndex;
  });
};

  const getType = localStorage.getItem("type") as string
  const UserIdentity = localStorage.getItem("UserId") as any;

  // Favourite add/remove — caller should ensure user is logged in before invoking
  const favaddremove = () => {
    if (!UserIdentity || !localStorage.getItem("Token")) {
      console.log("[AudioContext] favaddremove skipped — user not logged in");
      return;
    }
    if (!isLiked) {
      if (getType === "pravachans") {
        AudiosService.addPravachanFavourite(audioId).then((res) => {
          res.status && setIsLiked(true);
          setMessage(localStorage.getItem("lan")
            ? "ऑडियो को सफलतापूर्वक मेरी पसंद में जोड़ा गया है।"
            : "Audio has been successfully added to my favourites.")
        })
      } else if (getType === "DailySatsang") {
        AudiosService.addDailySatsangFavourite(audioId).then((res) => {
          res.status && setIsLiked(true);
          setMessage(localStorage.getItem("lan")
            ? "ऑडियो को सफलतापूर्वक मेरी पसंद में जोड़ा गया है।"
            : "Audio has been successfully added to my favourites.")
        })
      }
      else {
        AudiosService.addAudioFavourite(audioId).then((res) => {
          res.status && setIsLiked(true);
          setMessage(localStorage.getItem("lan")
            ? "ऑडियो को सफलतापूर्वक मेरी पसंद में जोड़ा गया है।"
            : "Audio has been successfully added to my favourites.")
        })
      }
    }
    else {
      if (getType === "pravachans") {
        AudiosService.removePravachanFaviourite(audioId).then((res) => {
          res.status && setIsLiked(false);
          setMessage(localStorage.getItem("lan")
            ? "ऑडियो को मेरी पसंद से हटा दिया गया है।"
            : "Audio has been removed from my favourites.");
        })
      } else if (getType === "DailySatsang") {
        AudiosService.removeDailySatsangFavourite(audioId).then((res) => {
          res.status && setIsLiked(false);
          setMessage(localStorage.getItem("lan")
            ? "ऑडियो को मेरी पसंद से हटा दिया गया है।"
            : "Audio has been removed from my favourites.");
        });
      } else {
        AudiosService.removeAudioFavourite(audioId).then((res) => {
          res.status && setIsLiked(false);
          setMessage(localStorage.getItem("lan")
            ? "ऑडियो को मेरी पसंद से हटा दिया गया है।"
            : "Audio has been removed from my favourites.");
        });
      };
    }
    setTimeout(() => setMessage(''), 5000);
  }

  const lyrt = () => {
    console.log("[AudioContext] lyrt called -> length", lyrtxt?.length);
    return lyrtxt;
  };

  const playbackspeed = () => {
    console.log("[AudioContext] playbackspeed setup called");
    var audio = new Audio();
    // try to play briefly so playbackRate is available in some browsers
    try { audio.play(); } catch (e) { /* ignore autoplay errors */ }
    var speedlist = document.getElementById("speedlist");
    speedlist?.addEventListener("change", changeSpeed);
    function changeSpeed(e: any) {
      console.log("[AudioContext] playbackspeed change ->", e?.target?.value);
      audio.playbackRate = e.target.value;
    }
    window.addEventListener("load", playbackspeed);
  };

  // Lyrics for logged-in and anonymous users
  const LoadLyrics = async (id: string, lyricsPath?: string, lyricsHash?: string) => {
    // Load lyrics script (only once) - request the actual JS file instead of the folder
    setTimeout(function () {
      if (!document.getElementById("rabbitlyrics")) {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.id = "rabbitlyrics";
        // point to the actual JS file in public/assets to avoid SPA HTML fallback
        script.src = "/assets/rabbitLyrics/rabbit-lyrics.umd.production.min.js";
        document.body.appendChild(script);
        script.onload = function () {
          /* intentionally empty - library exposes window.RabbitLyrics */
        };
      }
    }, 1000);

    try {
      const todayDate = new Date();
      const token = localStorage.getItem("Token") || "";
      const currentUserId = localStorage.getItem("UserId") || "";
      const signKey = localStorage.getItem("SignKey") || "";

      const timestamp = new Date().getTime();
      const encoded = btoa(timestamp.toString());

      const proc =
        localStorage.getItem("type") === "pravachans"
          ? `${process.env.REACT_APP_API_URL}/api/Pravachans/`
          : localStorage.getItem("type") === "DailySatsang"
            ? `${process.env.REACT_APP_API_URL}/api/DailySatsang/`
            : `${process.env.REACT_APP_API_URL}/api/Audios/`;

      // Format date part "yyyy-MM-dd HH:mm"
      const dvalue = todayDate.toISOString().replace('T', ' ').slice(0, 16);
      const medium = "Website";
      const headers: Record<string, string> = { 'X-Plateform': '1' };

      let requestUrl: string;

      if (currentUserId && signKey) {
        // Logged-in: original signed URL uses timestamp as t=
        requestUrl = proc + id + `/lyrics?t=` + encoded;
        const payload = `${currentUserId}|${dvalue}|${decodeURI(requestUrl)}|${signKey}|${medium}`;
        const hash = CryptoJS.HmacSHA256(payload, signKey);
        headers['X-Signature'] = CryptoJS.enc.Base64.stringify(hash);
        if (token) headers.Authorization = `Bearer ${token}`;
        console.log("[AudioContext] LoadLyrics logged-in signed request");
      } else {
        // Anonymous: use lyricsPath (hash token) from audio detail — no user SignKey
        requestUrl =
          lyricsPath ||
          (proc + id + `/lyrics?t=` + encodeURIComponent(lyricsHash || encoded));
        console.log("[AudioContext] LoadLyrics anonymous request", { requestUrl });
      }

      const resp = await fetch(requestUrl, { method: 'GET', headers });

      if (resp.ok) {
        const text = await resp.text();
        setlyrtxt(text);
        console.log("[AudioContext] LoadLyrics success", { length: text?.length });
      } else if (resp.status === 404) {
        setlyrtxt("");
      } else {
        // fetch (not ApiUtility) so 401 does not reload the page
        console.warn('[AudioContext] LoadLyrics fetch failed', resp.status);
        setlyrtxt("");
      }
    } catch (err) {
      console.error("[AudioContext] LoadLyrics error", err);
      setlyrtxt("");
    }
  };

  useEffect(() => {
    // Only make API call if user is logged in and there's a valid audioId
    console.log("[AudioContext] check favourite effect", { audioId, UserIdentity, token: localStorage.getItem("Token") });
    if (audioId && UserIdentity && localStorage.getItem("Token")) {
        AudiosService.getaudioandpravachanbyid(audioId).then((res) => {
        if (res.status) {
          setIsLiked(res?.result?.isFavourite)
        }
      }).catch(err => console.error("[AudioContext] getaudioandpravachanbyid error", err))
    } else {
      // Reset isLiked to false when no user or no audio
      console.log("[AudioContext] No audioId or UserIdentity, resetting isLiked to false");
      setIsLiked(false)
    }
  }, [audioId, UserIdentity]) // Remove isLiked from dependencies to prevent infinite loop

  useEffect(() => {
    console.log("[AudioContext] currentAudio changed", { currentAudio });
  }, [currentAudio]);

  useEffect(() => {
    if (audioIndex === undefined || NaN) {
      console.log("[AudioContext] audioIndex invalid, resetting to 0", { audioIndex });
      setAudioIndex(0)
    }
  }, [currentAudio, audioIndex, next, prev])

  const { slug } = useParams();

  useEffect(() => {
    const token = localStorage.getItem("Token");
    const currentType = localStorage.getItem("type");
    const logdetails: string[] = [];
    logdetails.push(`audioId=${audioId || "none"}`);
    logdetails.push(`Type=${currentType || "none"}`);
    logdetails.push(`slug=${slug || "none"}`);
    logdetails.push(`lastFetched=${lastFetchedAudioIdRef.current || "none"}`);
    console.log("[AudioContext] audio loader effect", logdetails.join(" | "), { token: !!token });
    // keep state in sync but read type from localStorage for immediate checks
    setType(currentType);

    // Stream load does not require Token; favourite status still gates on Token/UserId above
    if (!audioId || !currentType) {
      logdetails.push("action=skip_no_id_or_type");
      console.debug("[AudioContext]", logdetails.join(" | "));
      lastFetchedAudioIdRef.current = "";
      // clear lyrics when there's no audio
      setlyrtxt("");
      return;
    }

    // Same track already fetched — do not re-set currentAudio (would restart <audio> src)
    if (lastFetchedAudioIdRef.current === audioId) {
      logdetails.push("action=skip_already_fetched");
      console.log("[AudioContext]", logdetails.join(" | "));
      return;
    }

    setLoading(true);
    logdetails.push("action=fetch_by_id");
    console.log("[AudioContext]", logdetails.join(" | "));
    AudioPlayer.getAudiosById(audioId || `slug-${slug}`, currentType)
      .then((res) => {
        if (res.status && res.result) {
          lastFetchedAudioIdRef.current = res.result.id;
          // set current audio from API result
          setCurrentAudio({ ...res.result, playing: true });

          // Lyrics only when logged in; guests see login prompt in AudioLyrics
          const hasLyrics = !!(res.result?.lyricsPath || res.result?.lyricsHash);
          if (res.result?.id && hasLyrics && token) {
            console.warn("[LoadLyrics] calling for audioId", res.result.id);
            LoadLyrics(res.result.id, res.result.lyricsPath, res.result.lyricsHash);
          } else {
            console.warn("[LoadLyrics] skip", {
              audioId: res.result.id,
              hasLyrics,
              hasToken: !!token,
            });
            setlyrtxt("");
          }
        }
      })
      .catch((err) => console.error("[AudioContext] getAudiosById error", err))
      .finally(() => setLoading(false));
    // Intentionally omit `location` — route-only changes must not re-fetch and restart playback
  }, [audioId, slug]);

  return (
    <AudioContext.Provider
      value={{
        isMin,
        setMin,
        isMinimise,
        setIsMinismise,
        currentAudio,
        setCurrentAudio,
        playAudio,
        audiosList,
        setAudiosList,
        close,
        next,
        prev,
        isLiked,
        message,
        favaddremove,
        lyrt,
        playbackspeed,
        showList,
        setShowList,
        playingAudio,
        setPlayingAudio,
        playVideo,
        setPlayVideo,
        refAudio,
        state
        ,
        // expose raw lyrics text so consumers can render it
        lyricsText: lyrtxt,
        // After guest logs in from lyrics prompt, reload signed lyrics
        reloadLyrics: () => {
          const token = localStorage.getItem("Token");
          const audio = currentAudio;
          if (token && audio?.id && (audio.lyricsPath || audio.lyricsHash)) {
            console.log("[AudioContext] reloadLyrics after login", { id: audio.id });
            LoadLyrics(audio.id, audio.lyricsPath, audio.lyricsHash);
          } else {
            console.log("[AudioContext] reloadLyrics skipped", { hasToken: !!token, audioId: audio?.id });
          }
        },
        playPravachan: playAudio,
        setpravachanId,
        setMbAudioId
      }}
      {...props}
    />
  );
}

export function useAudio() {
  const context = React.useContext(AudioContext);
  if (context === undefined) {
    throw new Error(`useAudio must be used within a AudioProvider`);
  }
  return context;
}
